import { formatRef, parseRef, type RefEntity } from '@promanage/core';

/**
 * What a `/people/<something>` URL segment turned out to be.
 *
 * The URL carries the human-readable ref (PE000003), not the uuid, so a property manager
 * reading a code off a bond form can type it straight into the address bar. That means the
 * segment is user input and every wrong shape needs an answer, not a blank screen:
 * a ref for a different entity is a different message from a typo.
 *
 * ITS OWN MODULE because both `/people/$ref` routes call it from `beforeLoad`, which runs in the
 * eager route tree. Rollup tree-shakes across the whole graph, not per chunk, so while this lived
 * in `detail.ts` every helper the record page uses from there landed in the entry chunk with it.
 */
export type RefResolution =
  | { status: 'ok'; sequence: number; canonical: string }
  | { status: 'other-entity'; entity: RefEntity; canonical: string }
  | { status: 'malformed' };

export function resolvePersonRef(raw: string | undefined): RefResolution {
  const parsed = parseRef(raw ?? '');
  if (!parsed) return { status: 'malformed' };

  const canonical = formatRef(parsed.entity, parsed.sequence);
  if (parsed.entity !== 'person') {
    return { status: 'other-entity', entity: parsed.entity, canonical };
  }
  return { status: 'ok', sequence: parsed.sequence, canonical };
}
